import { FieldProps, getIn } from 'formik';
import { mdiAlertCircle } from '@mdi/js';
import * as React from 'react';
import { IBaseInputProps } from '../../input';
import * as IS from '../../input/input.styles';
import * as S from './phone.styles';

type PhoneFieldProps = IBaseInputProps & {
    field: FieldProps<any>;
};

export const PhoneField = React.memo<PhoneFieldProps>(props => {
    const {
        field: {
            field: { name, onBlur, value: fieldValue },
            form: { errors, touched: formTouched, setFieldValue },
        },
        readOnly,
        ...otherProps
    } = props;

    const error = getIn(errors, name);
    const touched = getIn(formTouched, name);
    const errorMessage = touched && error ? error : undefined;

    const extractDigits = (value: unknown): string => {
        if (value == null) return '';
        return String(value).replace(/\D/g, '');
    };

    const normalizeForMaskValue = (value: unknown): string => {
        const digits = extractDigits(value);
        if (!digits) {
            return '';
        }

        // IMask with "+{1} (000) 000-0000" expects unmasked digits.
        // Keep the tail to avoid malformed prefills.
        return digits.slice(-11);
    };

    const [stateValue, setStateValue] = React.useState(() => normalizeForMaskValue(fieldValue));

    React.useEffect(() => {
        setStateValue(normalizeForMaskValue(fieldValue));
    }, [fieldValue]);

    // --- sync ---
    const syncValue = (value: string) => {
        const digits = normalizeForMaskValue(value);
        if (!digits) {
            setFieldValue(name, '');
            return;
        }

        setFieldValue(name, `+${digits}`);
    };

    // ONLY IMASK SOURCE OF TRUTH
    const handleAccept = (value: unknown) => {
        const v = String(value || '');
        const digits = normalizeForMaskValue(v);
        setStateValue(digits);
        syncValue(digits);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        onBlur(e);
    };

    return (
        <IS.InputContainer>
            <IS.InputWrapper>
                <S.StyledMaskInput
                    autoComplete="tel"
                    mask="+{1} (000) 000-0000"
                    unmask={true}
                    value={stateValue}
                    onAccept={handleAccept}
                    onBlur={handleBlur}
                    readOnly={readOnly}
                    name={name}
                    showError={!!errorMessage}
                    {...otherProps}
                />
            </IS.InputWrapper>

            {errorMessage && (
                <IS.InputBottomError>
                    <IS.InputBottomIcon path={mdiAlertCircle} />
                    <IS.InputBottomMessage>{errorMessage}</IS.InputBottomMessage>
                </IS.InputBottomError>
            )}
        </IS.InputContainer>
    );
});
